import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ChildDashboard({ user, onLogout }) {
  const [books, setBooks] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [activeTab, setActiveTab] = useState('books');
  const [selectedBook, setSelectedBook] = useState(null);
  const [review, setReview] = useState({ text: '', rating: 5 });
  const [notification, setNotification] = useState(null);
  const [viewReviews, setViewReviews] = useState(null);
  const [bookReviews, setBookReviews] = useState([]);
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchBooks();
    fetchInventory();
  }, []);

  const fetchBooks = async () => {
    setLoadingBooks(true);
    try {
      const response = await fetch('https://lupa.nekk.it/api/books');
      const data = await response.json();
      setBooks(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingBooks(false);
    }
  };

  const fetchInventory = async () => {
    setLoadingInventory(true);
    try {
      const response = await fetch(`https://lupa.nekk.it/api/users/${user.id}/inventory`);
      const data = await response.json();
      setInventory(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoadingInventory(false);
    }
  };

  const requestBook = async (bookId) => {
    try {
      const response = await fetch('https://lupa.nekk.it/api/loans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, book_id: bookId }),
      });

      if (response.ok) {
        setNotification({ type: 'success', message: '¡Libro solicitado con éxito! 🎉' });
        setTimeout(() => setNotification(null), 3000);
        fetchBooks();
        fetchInventory();
      } else {
        const data = await response.json();
        setNotification({ type: 'error', message: data.error || 'Error al solicitar' });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al solicitar el libro 😔' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  const fetchBookReviews = async (bookId) => {
    setLoadingReviews(true);
    try {
      const response = await fetch(`https://lupa.nekk.it/api/books/${bookId}/reviews`);
      const data = await response.json();
      setBookReviews(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error:', error);
      setBookReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const openReviewsModal = async (book) => {
    setViewReviews(book);
    await fetchBookReviews(book.id);
  };

  const submitReview = async () => {
    if (!selectedBook) return;

    try {
      // Enviar reseña
      const response = await fetch('https://lupa.nekk.it/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          book_id: selectedBook.book.id,
          review: review.text,
          rating: review.rating,
        }),
      });

      if (response.ok) {
        // Devolver libro automáticamente
        await fetch(`https://lupa.nekk.it/api/loans/${selectedBook.id}/return`, {
          method: 'PUT',
        });
        
        setNotification({ type: 'success', message: '¡Reseña enviada y libro devuelto! 📚✨' });
        setTimeout(() => setNotification(null), 3000);
        setSelectedBook(null);
        setReview({ text: '', rating: 5 });
        fetchBooks();
        fetchInventory();
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'Error al enviar la reseña 😔' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-300 via-orange-300 to-pink-300 flex flex-col">
      <nav className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 shadow-2xl">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-3xl font-black flex items-center gap-2">
            🔍 Lupa Digital - ¡Hola {user.name}!
          </h1>
          <button
            onClick={onLogout}
            className="bg-red-500 hover:bg-red-600 px-6 py-3 rounded-full font-bold shadow-lg transform hover:scale-105 transition"
          >
            Salir
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto p-6 w-full flex-grow">
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('books')}
            className={`px-8 py-4 rounded-2xl font-black text-lg transform hover:scale-105 transition shadow-lg ${
              activeTab === 'books'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                : 'bg-white text-gray-700'
            }`}
          >
            📚 Libros Disponibles
          </button>
          <button
            onClick={() => setActiveTab('inventory')}
            className={`px-8 py-4 rounded-2xl font-black text-lg transform hover:scale-105 transition shadow-lg ${
              activeTab === 'inventory'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white'
                : 'bg-white text-gray-700'
            }`}
          >
            🎒 Mi Mochila de Libros
          </button>
        </div>

        {activeTab === 'books' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {loadingBooks ? (
              // Skeleton Loading
              [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-2xl p-4 border-4 border-purple-300 animate-pulse">
                  <div className="w-full h-40 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                  </div>
                </div>
              ))
            ) : (
              books.map((book) => (
              <div key={book.id} className="bg-white rounded-2xl shadow-2xl p-4 transform hover:scale-105 transition border-4 border-purple-300">
                {book.image_url && (
                  <img
                    src={book.image_url}
                    alt={book.title}
                    className="w-full h-40 object-contain rounded-xl mb-3 bg-gradient-to-br from-yellow-50 to-pink-50"
                  />
                )}
                <h3 className="text-lg font-black mb-1 line-clamp-2 text-purple-800">{book.title}</h3>
                <p className="text-sm text-gray-600 mb-2 line-clamp-1 font-semibold">✍️ {book.author}</p>
                {book.avg_rating > 0 && (
                  <p className="text-orange-600 text-base font-bold mb-2">
                    {'🔍'.repeat(Math.round(book.avg_rating))}
                  </p>
                )}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-2xl">
                      {book.in_stock ? '🟢' : '🔴'}
                    </span>
                    {book.in_stock && (
                      <button
                        onClick={() => requestBook(book.id)}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg transform hover:scale-110 transition"
                      >
                        ¡Quiero este!
                      </button>
                    )}
                  </div>
                  <button
                    onClick={() => openReviewsModal(book)}
                    className="w-full bg-gradient-to-r from-orange-400 to-yellow-500 hover:from-orange-500 hover:to-yellow-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg transform hover:scale-105 transition"
                  >
                    🔍 Ver reseñas
                  </button>
                </div>
              </div>
))
            )}
          </div>
        )}

        {activeTab === 'inventory' && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {loadingInventory ? (
              // Skeleton Loading
              [1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-2xl p-4 border-4 border-blue-300 animate-pulse">
                  <div className="w-full h-40 bg-gradient-to-r from-gray-200 to-gray-300 rounded-xl mb-3"></div>
                  <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-2"></div>
                  <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded mb-3 w-3/4"></div>
                  <div className="space-y-2">
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                    <div className="h-8 bg-gradient-to-r from-gray-200 to-gray-300 rounded-full"></div>
                  </div>
                </div>
              ))
            ) : inventory.length === 0 ? (
              <p className="text-gray-700 text-xl font-bold">No tienes libros en tu mochila.</p>
            ) : (
              inventory.map((loan) => (
                <div key={loan.id} className="bg-white rounded-2xl shadow-2xl p-4 border-4 border-blue-300">
                  {loan.book?.image_url && (
                    <img
                      src={loan.book.image_url}
                      alt={loan.book.title}
                      className="w-full h-40 object-contain rounded-xl mb-3 bg-gradient-to-br from-blue-50 to-purple-50"
                    />
                  )}
                  <h3 className="text-lg font-black mb-1 line-clamp-2 text-blue-800">{loan.book?.title}</h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-1 font-semibold">✍️ {loan.book?.author}</p>
                  <p className="text-xs text-gray-500 mb-3 font-semibold">
                    📅 {new Date(loan.borrowed_at).toLocaleDateString()}
                  </p>
                  <div className="space-y-2">
                    <button
                      onClick={() => setSelectedBook(loan)}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-3 py-2 rounded-full text-sm font-bold shadow-lg transform hover:scale-105 transition"
                    >
                      🔍 ¡Hacer Reseña!
                    </button>
                    <button
                      onClick={() => openReviewsModal(loan.book)}
                      className="w-full bg-gradient-to-r from-orange-400 to-yellow-500 hover:from-orange-500 hover:to-yellow-600 text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg transform hover:scale-105 transition"
                    >
                      👀 Ver reseñas
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {notification && (
        <div className="fixed top-4 right-4 z-50 animate-bounce">
          <div className={`px-6 py-4 rounded-2xl shadow-2xl font-bold text-white ${
            notification.type === 'success' 
              ? 'bg-gradient-to-r from-green-500 to-green-600' 
              : 'bg-gradient-to-r from-red-500 to-red-600'
          }`}>
            {notification.message}
            <button
              onClick={() => setNotification(null)}
              className="ml-4 text-white hover:text-gray-200"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {viewReviews && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl p-8 w-full max-w-2xl shadow-2xl border-4 border-blue-400 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl font-black text-blue-800">
                🔍 Reseñas de "{viewReviews.title}"
              </h2>
              <button
                onClick={() => setViewReviews(null)}
                className="text-gray-600 hover:text-gray-800 text-3xl font-bold"
              >
                ✕
              </button>
            </div>
            {bookReviews.length === 0 && !loadingReviews ? (
              <p className="text-center text-gray-600 text-lg font-semibold py-8">
                Todavía no hay reseñas para este libro 😔
              </p>
            ) : loadingReviews ? (
              // Skeleton Loading para reseñas
              [1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200 animate-pulse">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="h-5 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-32 mb-2"></div>
                      <div className="h-4 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-20"></div>
                    </div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-16"></div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-full"></div>
                    <div className="h-3 bg-gradient-to-r from-gray-200 to-gray-300 rounded w-5/6"></div>
                  </div>
                </div>
              ))
            ) : (
              <div className="space-y-4">
                {Array.isArray(bookReviews) && bookReviews.map((review) => (
                  <div key={review.id} className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-black text-lg text-purple-800">👤 {review.user?.name}</p>
                        <p className="text-orange-600 text-xl">
                          {'🔍'.repeat(review.rating)}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <p className="text-gray-700 text-base">{review.review}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {selectedBook && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-3xl p-8 w-full max-w-md shadow-2xl border-4 border-purple-400">
            <h2 className="text-3xl font-black mb-4 text-purple-800">
              🔍 Tu opinión de "{selectedBook.book?.title}"
            </h2>
            <div className="mb-4">
              <label className="block text-lg font-black mb-2 text-purple-700">
                ¿Cuántas lupas le das? (1-5 lupas 🔍)
              </label>
              <div className="flex gap-2 mb-2">
                {[1, 2, 3, 4, 5].map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setReview({ ...review, rating: num })}
                    className={`text-4xl transform hover:scale-125 transition ${
                      review.rating >= num ? 'opacity-100' : 'opacity-30'
                    }`}
                  >
                    🔍
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-lg font-black mb-2 text-purple-700">
                ¿Qué te pareció?
              </label>
              <textarea
                value={review.text}
                onChange={(e) => setReview({ ...review, text: e.target.value })}
                className="w-full px-4 py-3 border-4 border-purple-300 rounded-2xl h-32 text-lg"
                placeholder="Cuéntanos qué te gustó..."
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={submitReview}
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-6 py-3 rounded-full font-black text-lg shadow-lg transform hover:scale-105 transition"
              >
                ✅ Enviar
              </button>
              <button
                onClick={() => setSelectedBook(null)}
                className="flex-1 bg-gray-400 hover:bg-gray-500 text-white px-6 py-3 rounded-full font-black text-lg shadow-lg transform hover:scale-105 transition"
              >
                ❌ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-gradient-to-r from-purple-600 to-pink-600 text-white py-6 mt-auto w-full">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-lg font-bold">✨ Creado por Ariadna Nadal ✨</p>
        </div>
      </footer>
    </div>
  );
}
